import React, { useEffect, useState, useCallback, useMemo } from 'react';
import './RankHistoryWatch.css';
import {
    Card,
    Input,
    Button,
    Modal,
    Spin,
    Empty,
    Space,
    Typography,
    message,
    Row,
    Col,
    Tag,
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    ReloadOutlined,
    LineChartOutlined,
    DownOutlined,
    UpOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
    ResponsiveContainer,
} from 'recharts';
import AuthService from '../../services/authService';
import ProtectedMenuPage from '../../components/ProtectedMenuPage/ProtectedMenuPage';
import { useQuotaStatus } from '../../hooks/useQuotaStatus';
import { dispatchQuotaUpdated } from '../../utils/quotaEvent';
import { apiErrToMessage } from '../../utils/apiError';
import KakaoFloatButton from '../../components/KakaoFloatButton/KakaoFloatButton';

const { Text } = Typography;

function extractMid(url) {
    const m1 = (url || '').match(/\/products\/(\d+)/);
    if (m1) return m1[1];
    const m2 = (url || '').match(/\/catalog\/(\d+)/);
    return m2 ? m2[1] : null;
}

function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}-${dd}`;
}

function buildRankDisplay(data) {
    if (!Array.isArray(data) || data.length === 0) return [];
    const sorted = [...data].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 30);
    return sorted.map((item, idx) => {
        const prev = sorted[idx + 1];
        let diff = null;
        if (prev && item.rank && prev.rank) {
            diff = prev.rank - item.rank;
        }
        return { ...item, diff };
    });
}

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const rank = payload[0]?.value;
    return (
        <div
            style={{
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: '10px 14px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}
        >
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: rank ? '#4f46e5' : '#94a3b8' }}>
                {rank ? `${rank}위` : '순위밖'}
            </div>
        </div>
    );
};

const CustomDot = props => {
    const { cx, cy, payload } = props;
    if (!payload?.rank) return null;
    return <circle cx={cx} cy={cy} r={4} fill="#4f46e5" stroke="white" strokeWidth={1.5} />;
};

const CustomXAxisTick = ({ x, y, payload, data }) => {
    if (!payload || !payload.value) return null;
    const item = data.find(d => d.date === payload.value);
    if (!item) return null;

    let changeEl = (
        <tspan fill="#cbd5e1" fontSize="9">
            -
        </tspan>
    );
    if (item.diff !== null && item.diff !== undefined) {
        if (item.diff > 0) {
            changeEl = (
                <tspan fill="#ef4444" fontWeight="700" fontSize="9">
                    ▲{item.diff}
                </tspan>
            );
        } else if (item.diff < 0) {
            changeEl = (
                <tspan fill="#3b82f6" fontWeight="700" fontSize="9">
                    ▼{Math.abs(item.diff)}
                </tspan>
            );
        }
    }

    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={0}
                dy={12}
                textAnchor="middle"
                fill="#64748b"
                style={{ fontSize: 10, fontWeight: 500 }}
            >
                {item.date}
            </text>
            <text
                x={0}
                y={0}
                dy={24}
                textAnchor="middle"
                fill={item.rank ? '#4f46e5' : '#94a3b8'}
                style={{ fontSize: 10, fontWeight: item.rank ? 700 : 400 }}
            >
                {item.rank ? `${item.rank}위` : '-'}
            </text>
            <text x={0} y={0} dy={36} textAnchor="middle">
                {changeEl}
            </text>
        </g>
    );
};

function WatchCard({
    row,
    rankData,
    loading,
    onDelete,
    onRefresh,
    collapsed,
    onToggleCollapse,
    isSample = false,
}) {
    const product = rankData?.product || null;

    const display = useMemo(() => {
        return buildRankDisplay(rankData?.series);
    }, [rankData]);

    // Statistics calculations
    const stats = useMemo(() => {
        if (!display || display.length === 0) return null;

        // Filter out items with no rank (null or 0)
        const validRanks = display.filter(item => item.rank && item.rank > 0);
        const rankValues = validRanks.map(item => item.rank);

        const latestItem = display[0]; // Newest first
        const latestRank = latestItem?.rank || null;
        const latestDiff = latestItem?.diff || null;

        const bestRank = rankValues.length > 0 ? Math.min(...rankValues) : null;
        const worstRank = rankValues.length > 0 ? Math.max(...rankValues) : null;
        const avgRank =
            rankValues.length > 0
                ? Math.round(rankValues.reduce((sum, r) => sum + r, 0) / rankValues.length)
                : null;

        return {
            latestRank,
            latestDiff,
            latestIsToday: latestItem?.isToday,
            bestRank,
            worstRank,
            avgRank,
            totalCount: validRanks.length,
        };
    }, [display]);

    // Chronological order for chart
    const chartData = useMemo(() => {
        if (!display || display.length === 0) return [];
        return [...display].reverse().map(item => ({
            date: formatDate(item.date),
            rank: item.rank || null,
            isToday: item.isToday || false,
            diff: item.diff !== undefined ? item.diff : null,
        }));
    }, [display]);

    const hasValidRanks = useMemo(() => {
        if (!rankData?.series || rankData.series.length === 0) return false;
        return rankData.series.some(item => item.rank && item.rank > 0);
    }, [rankData]);

    const gradientId = `rankGradient_${row.id}`;

    // We calculate domain dynamically
    const chartDomain = useMemo(() => {
        if (!stats) return [1, 10];
        const maxRank = stats.worstRank || 10;
        const bottomPadding = Math.max(2, Math.ceil(maxRank * 0.1));
        return [1, maxRank + bottomPadding];
    }, [stats]);

    const handleDelete = () => {
        Modal.confirm({
            title: '항목 삭제',
            content: `"${row.memo || row.keyword}" 항목을 삭제하시겠습니까?`,
            okText: '삭제',
            okType: 'danger',
            cancelText: '취소',
            onOk: () => onDelete(row.id),
        });
    };

    return (
        <Card
            className="watch-card-item"
            size="small"
            style={{
                marginBottom: 28,
                borderRadius: 10,
                border: '1.5px solid #cbd5e1',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
                position: 'relative',
            }}
            headStyle={{
                background: '#f1f5f9',
                borderBottom: collapsed ? 'none' : '1.5px solid #cbd5e1',
                minHeight: '42px',
                padding: '0 16px',
            }}
            bodyStyle={{ padding: collapsed ? 0 : '16px', display: collapsed ? 'none' : 'block' }}
            title={
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '12px',
                        flexWrap: 'wrap',
                        paddingRight: '8px',
                    }}
                >
                    <Space size={8} wrap>
                        <span style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
                            {row.keyword}
                        </span>
                        {isSample && (
                            <Tag
                                color="blue"
                                style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    margin: 0,
                                    borderRadius: 4,
                                    border: 'none',
                                    background: '#e0f2fe',
                                    color: '#0369a1',
                                }}
                            >
                                예시
                            </Tag>
                        )}
                        {!isSample && (
                            <div
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    background: '#f8fafc',
                                    padding: '2px 8px',
                                    borderRadius: 4,
                                    border: '1px solid #e2e8f0',
                                    fontSize: 11,
                                    color: '#64748b',
                                    height: '22px',
                                }}
                            >
                                <span
                                    style={{
                                        borderRight: '1px solid #cbd5e1',
                                        paddingRight: 10,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    상품번호:{' '}
                                    <b style={{ marginLeft: 4, color: '#334155' }}>
                                        {product?.product_id || row.product_mid}
                                    </b>
                                </span>
                                <span
                                    style={{
                                        borderRight: '1px solid #cbd5e1',
                                        paddingRight: 10,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    MID:{' '}
                                    <b style={{ marginLeft: 4, color: '#334155' }}>
                                        {product?.product_mid || row.product_mid}
                                    </b>
                                </span>
                                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                    상품링크:
                                    <a
                                        href={
                                            row.product_url ||
                                            `https://smartstore.naver.com/main/products/${product?.product_mid || row.product_mid}`
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            color: '#4f46e5',
                                            fontWeight: 600,
                                            textDecoration: 'underline',
                                            marginLeft: 4,
                                        }}
                                    >
                                        바로가기
                                    </a>
                                </span>
                            </div>
                        )}
                    </Space>

                    {product?.updated_at && (
                        <Tag
                            color="orange"
                            style={{
                                fontSize: 10,
                                fontWeight: 700,
                                margin: 0,
                                borderRadius: 4,
                                border: 'none',
                                background: '#ffe8cc',
                                color: '#d9480f',
                            }}
                        >
                            조회기준: {product.updated_at} (최근30일)
                        </Tag>
                    )}
                </div>
            }
            extra={
                isSample ? null : (
                    <Space size={8}>
                        <Button
                            size="small"
                            icon={collapsed ? <DownOutlined /> : <UpOutlined />}
                            onClick={onToggleCollapse}
                            style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                borderRadius: '12px',
                                background: '#f1f5f9',
                                border: '1px solid #cbd5e1',
                                color: '#475569',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '24px',
                                width: '76px',
                                padding: 0,
                            }}
                        >
                            {collapsed ? '펼치기' : '접기'}
                        </Button>
                        <Button
                            size="small"
                            icon={
                                <ReloadOutlined style={{ color: '#4f46e5', fontWeight: 'bold' }} />
                            }
                            onClick={onRefresh}
                            style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                borderRadius: '12px',
                                background: '#eef2ff',
                                border: '1px solid #c7d2fe',
                                color: '#4f46e5',
                                display: 'inline-flex',
                                alignItems: 'center',
                                height: '24px',
                                padding: '0 8px',
                            }}
                        >
                            새로고침(차감)
                        </Button>
                        <Button
                            size="small"
                            icon={<DeleteOutlined style={{ color: '#ef4444' }} />}
                            onClick={handleDelete}
                            style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                borderRadius: '12px',
                                background: '#fef2f2',
                                border: '1px solid #fecaca',
                                color: '#ef4444',
                                display: 'inline-flex',
                                alignItems: 'center',
                                height: '24px',
                                padding: '0 8px',
                            }}
                        >
                            삭제
                        </Button>
                    </Space>
                )
            }
        >
            <style>{`
                .watch-card-item {
                    transition: all 0.2s ease-in-out !important;
                }
                .watch-card-item:hover {
                    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.08) !important;
                    border-color: #94a3b8 !important;
                }
                .highlighted-title b {
                    color: #4f46e5 !important;
                    font-weight: 700;
                }
            `}</style>
            <Spin spinning={loading} size="default">
                {isSample && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '55%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            fontSize: '68px',
                            fontWeight: 900,
                            color: 'rgba(148, 163, 184, 0.18)',
                            pointerEvents: 'none',
                            zIndex: 10,
                            whiteSpace: 'nowrap',
                            letterSpacing: '10px',
                            userSelect: 'none',
                        }}
                    >
                        과거 30일 순위
                    </div>
                )}
                {!rankData ? (
                    <div
                        style={{
                            textAlign: 'center',
                            padding: 40,
                            minHeight: 180,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Empty description="데이터 없음" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </div>
                ) : (
                    <>
                        {/* 상품 대표 정보 */}
                        {product && (
                            <div
                                style={{
                                    display: 'flex',
                                    gap: 12,
                                    marginBottom: 12,
                                    alignItems: 'flex-start',
                                }}
                            >
                                {product.image && (
                                    <img
                                        src={product.image}
                                        alt={product.title}
                                        style={{
                                            width: 56,
                                            height: 56,
                                            objectFit: 'cover',
                                            borderRadius: 6,
                                            border: '1px solid #f1f5f9',
                                            flexShrink: 0,
                                        }}
                                    />
                                )}
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    {product.title && (
                                        <div
                                            className="highlighted-title"
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                marginBottom: 4,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {product.link && !isSample ? (
                                                <a
                                                    href={product.link}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    style={{ color: '#1e293b' }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: product.title,
                                                    }}
                                                />
                                            ) : (
                                                <span
                                                    style={{ color: '#1e293b' }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: product.title,
                                                    }}
                                                />
                                            )}
                                        </div>
                                    )}
                                    <Space size={6} wrap>
                                        {product.lprice && (
                                            <Text
                                                style={{
                                                    fontSize: 12,
                                                    color: '#ef4444',
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {Number(product.lprice).toLocaleString()}원
                                            </Text>
                                        )}
                                        {product.mall_name && (
                                            <Tag
                                                style={{
                                                    fontSize: 10,
                                                    margin: 0,
                                                    background: '#f1f5f9',
                                                    border: 'none',
                                                    color: '#64748b',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {product.mall_name}
                                            </Tag>
                                        )}
                                        {product.category1 && (
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                {[
                                                    product.category1,
                                                    product.category2,
                                                    product.category3,
                                                    product.category4,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' > ')}
                                            </Text>
                                        )}
                                    </Space>
                                </div>
                            </div>
                        )}

                        {/* 통계 지표 카드들 */}
                        {stats && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                    marginBottom: '16px',
                                }}
                            >
                                <div
                                    style={{
                                        flex: '1 1 120px',
                                        padding: '10px 12px',
                                        background: '#f8fafc',
                                        borderRadius: '8px',
                                        border: '1px solid #f1f5f9',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: '11px',
                                            color: '#64748b',
                                            fontWeight: 500,
                                        }}
                                    >
                                        조회기준일 마지막 순위
                                    </span>
                                    <span
                                        style={{
                                            fontSize: '14px',
                                            fontWeight: 700,
                                            color: '#0f172a',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            marginTop: '2px',
                                        }}
                                    >
                                        {stats.latestRank ? `${stats.latestRank}위` : '순위 없음'}
                                        {stats.latestDiff !== null &&
                                            (stats.latestDiff > 0 ? (
                                                <span
                                                    style={{
                                                        color: '#ef4444',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    ▲{stats.latestDiff}
                                                </span>
                                            ) : stats.latestDiff < 0 ? (
                                                <span
                                                    style={{
                                                        color: '#3b82f6',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    ▼{Math.abs(stats.latestDiff)}
                                                </span>
                                            ) : (
                                                <span
                                                    style={{ color: '#94a3b8', fontSize: '12px' }}
                                                >
                                                    -
                                                </span>
                                            ))}
                                    </span>
                                </div>

                                <div
                                    style={{
                                        flex: '1 1 120px',
                                        padding: '10px 12px',
                                        background: '#f0fdf4',
                                        borderRadius: '8px',
                                        border: '1px solid #dcfce7',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: '11px',
                                            color: '#16a34a',
                                            fontWeight: 500,
                                        }}
                                    >
                                        최고 순위
                                    </span>
                                    <span
                                        style={{
                                            fontSize: '14px',
                                            fontWeight: 700,
                                            color: '#15803d',
                                            marginTop: '2px',
                                        }}
                                    >
                                        {stats.bestRank ? `${stats.bestRank}위` : '-'}
                                    </span>
                                </div>

                                <div
                                    style={{
                                        flex: '1 1 120px',
                                        padding: '10px 12px',
                                        background: '#fef2f2',
                                        borderRadius: '8px',
                                        border: '1px solid #fee2e2',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: '11px',
                                            color: '#dc2626',
                                            fontWeight: 500,
                                        }}
                                    >
                                        최저 순위
                                    </span>
                                    <span
                                        style={{
                                            fontSize: '14px',
                                            fontWeight: 700,
                                            color: '#b91c1c',
                                            marginTop: '2px',
                                        }}
                                    >
                                        {stats.worstRank ? `${stats.worstRank}위` : '-'}
                                    </span>
                                </div>

                                <div
                                    style={{
                                        flex: '1 1 120px',
                                        padding: '10px 12px',
                                        background: '#fffbeb',
                                        borderRadius: '8px',
                                        border: '1px solid #fef3c7',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: '11px',
                                            color: '#d97706',
                                            fontWeight: 500,
                                        }}
                                    >
                                        평균 순위
                                    </span>
                                    <span
                                        style={{
                                            fontSize: '14px',
                                            fontWeight: 700,
                                            color: '#b45309',
                                            marginTop: '2px',
                                        }}
                                    >
                                        {stats.avgRank ? `${stats.avgRank}위` : '-'}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* 이력 제어 바 */}
                        {!collapsed && (
                            <>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        marginBottom: 12,
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: '#4f46e5',
                                    }}
                                >
                                    <LineChartOutlined />
                                    <span>
                                        순위 변동 이력{' '}
                                        <span
                                            style={{
                                                fontSize: '11px',
                                                color: '#94a3b8',
                                                fontWeight: 400,
                                            }}
                                        >
                                            (최근 30일)
                                        </span>
                                    </span>
                                </div>

                                {/* 순위 콘텐츠 */}
                                {!hasValidRanks ? (
                                    <div
                                        style={{
                                            minHeight: '280px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: '#f8fafc',
                                            borderRadius: '8px',
                                            border: '1px dashed #cbd5e1',
                                            flexDirection: 'column',
                                            gap: '6px',
                                            padding: '24px 16px',
                                        }}
                                    >
                                        <InfoCircleOutlined
                                            style={{
                                                fontSize: '28px',
                                                color: '#94a3b8',
                                                marginBottom: '4px',
                                            }}
                                        />
                                        <Text
                                            style={{
                                                fontSize: '14px',
                                                fontWeight: 700,
                                                color: '#334155',
                                            }}
                                        >
                                            최근 30일간의 순위 데이터가 없습니다.
                                        </Text>
                                        <div
                                            style={{
                                                fontSize: '12px',
                                                color: '#64748b',
                                                marginTop: '6px',
                                                textAlign: 'center',
                                                lineHeight: '1.7',
                                            }}
                                        >
                                            <div>• 쿼터는 차감되지 않았습니다.</div>
                                            <div>
                                                • 네이버 쇼핑 <b>100위 이내</b> 상품만 조회가
                                                가능합니다.
                                            </div>
                                            <div>
                                                • 맞춤법·오타가 없는 <b>정확한 대표 키워드</b>로
                                                다시 시도해 보세요.
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ width: '100%', height: 280, marginTop: 8 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart
                                                data={chartData}
                                                margin={{
                                                    top: 15,
                                                    right: 30,
                                                    left: -20,
                                                    bottom: 42,
                                                }}
                                            >
                                                <defs>
                                                    <linearGradient
                                                        id={gradientId}
                                                        x1="0"
                                                        y1="0"
                                                        x2="0"
                                                        y2="1"
                                                    >
                                                        <stop
                                                            offset="5%"
                                                            stopColor="#4f46e5"
                                                            stopOpacity={0.22}
                                                        />
                                                        <stop
                                                            offset="95%"
                                                            stopColor="#4f46e5"
                                                            stopOpacity={0}
                                                        />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid
                                                    strokeDasharray="3 3"
                                                    stroke="#f1f5f9"
                                                    vertical={false}
                                                />
                                                <XAxis
                                                    dataKey="date"
                                                    tick={<CustomXAxisTick data={chartData} />}
                                                    axisLine={false}
                                                    tickLine={false}
                                                    interval={0}
                                                />
                                                <YAxis
                                                    reversed={true}
                                                    domain={chartDomain}
                                                    tick={{ fontSize: 10, fill: '#94a3b8' }}
                                                    tickFormatter={v => `${v}위`}
                                                    axisLine={false}
                                                    tickLine={false}
                                                />
                                                <Tooltip content={<CustomTooltip />} />
                                                {stats.avgRank && (
                                                    <ReferenceLine
                                                        y={stats.avgRank}
                                                        stroke="#f59e0b"
                                                        strokeDasharray="4 3"
                                                        strokeWidth={1.2}
                                                        label={{
                                                            value: `평균 ${stats.avgRank}위`,
                                                            fill: '#f59e0b',
                                                            fontSize: 9,
                                                            position: 'insideBottomRight',
                                                            dy: 14,
                                                        }}
                                                    />
                                                )}
                                                <Area
                                                    type="monotone"
                                                    dataKey="rank"
                                                    stroke="#4f46e5"
                                                    strokeWidth={2}
                                                    fill={`url(#${gradientId})`}
                                                    baseValue={chartDomain[1]}
                                                    connectNulls={true}
                                                    dot={<CustomDot />}
                                                    activeDot={{
                                                        r: 5,
                                                        fill: '#4f46e5',
                                                        stroke: 'white',
                                                        strokeWidth: 1.5,
                                                    }}
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </Spin>
        </Card>
    );
}

const getSampleSeriesData = () => {
    const series = [];
    const today = new Date();
    for (let i = 0; i < 30; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        const dateStr = d.toISOString().split('T')[0];
        const x = 29 - i;
        let rank = Math.round(2 + 66 * Math.pow(1 - x / 29, 2.2));
        if (x > 0 && x < 29) {
            const noise = (x % 3 === 0 ? 1 : x % 3 === 1 ? -1 : 0) * (x > 15 ? 2 : 1);
            rank = Math.max(1, rank + noise);
        }
        series.push({
            date: dateStr,
            rank: rank,
            isToday: i === 0,
        });
    }
    return series;
};

const sampleRankData = {
    product: {
        title: '<b>[1+1] 감성 캠핑 LED 랜턴 충전식 야외 조명등 (랜턴 예시)</b>',
        link: 'https://smartstore.naver.com/main/products/12345678901',
        image: `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
  <rect width="100" height="100" fill="#f1f5f9" rx="8"/>
  <circle cx="50" cy="50" r="22" fill="#fef08a" opacity="0.8"/>
  <path d="M35 75 h30 v-8 h-30 z M40 67 h20 v-30 h-20 z M30 37 h40 l-10 -15 h-20 z" fill="#475569"/>
  <path d="M40 22 Q50 8 60 22" fill="none" stroke="#475569" stroke-width="3" stroke-linecap="round"/>
  <circle cx="50" cy="50" r="8" fill="#eab308"/>
</svg>
        `)}`,
        lprice: '29800',
        mall_name: 'ADCARE 공식스토어',
        category1: '스포츠/레저',
        category2: '캠핑',
        category3: '야외조명',
        category4: '랜턴',
        updated_at: '오늘 조회완료',
    },
    series: getSampleSeriesData(),
};

export default function RankHistoryWatch() {
    const [rows, setRows] = useState([]);
    const [maxRows, setMaxRows] = useState(20);
    const [listLoading, setListLoading] = useState(false);
    const [rankDataMap, setRankDataMap] = useState({});
    const [rankLoadingMap, setRankLoadingMap] = useState({});
    useQuotaStatus('rankHistoryWatch');
    const [collapsedMap, setCollapsedMap] = useState(() => {
        try {
            const saved = localStorage.getItem('rank_history_collapsed_map');
            return saved ? JSON.parse(saved) : {};
        } catch {
            return {};
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem('rank_history_collapsed_map', JSON.stringify(collapsedMap));
        } catch (e) {
            console.error('Failed to save collapsed states', e);
        }
    }, [collapsedMap]);

    const handleCollapseAll = () => {
        const newMap = {};
        rows.forEach(r => {
            newMap[r.id] = true;
        });
        setCollapsedMap(newMap);
    };

    const handleExpandAll = () => {
        setCollapsedMap({});
    };

    const [urlInput, setUrlInput] = useState('');
    const [keywordInput, setKeywordInput] = useState('');
    const [addLoading, setAddLoading] = useState(false);

    const loadBatch = useCallback(async currentRows => {
        if (!currentRows.length) {
            setRankDataMap({});
            return;
        }

        // Mark all current rows as loading
        const initialMap = {};
        currentRows.forEach(r => {
            initialMap[`${r.keyword}__${r.product_mid}`] = true;
        });
        setRankLoadingMap(initialMap);

        const startTime = Date.now();
        try {
            const items = currentRows.map(r => ({
                keyword: r.keyword,
                mid: r.product_mid,
                url: r.product_url,
            }));
            const res = await AuthService.authAxios.post('/product/rank-history-batch', { items });
            if (res.data?.success) {
                setRankDataMap(res.data.results || {});
                dispatchQuotaUpdated('rankHistoryWatch');
            }
        } catch (err) {
            dispatchQuotaUpdated('rankHistoryWatch');
            const msg = apiErrToMessage(err?.response?.data, err?.response?.status);
            message.error(msg);
        } finally {
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, 1000 - elapsed);
            setTimeout(() => {
                setRankLoadingMap({});
            }, remaining);
        }
    }, []);

    const loadSingleRank = useCallback(async (keyword, mid, url) => {
        const key = `${keyword}__${mid}`;
        setRankLoadingMap(prev => ({ ...prev, [key]: true }));
        const startTime = Date.now();
        try {
            const items = [{ keyword, mid, url }];
            const res = await AuthService.authAxios.post('/product/rank-history-batch', {
                items,
                force: true,
            });
            if (res.data?.success) {
                const results = res.data.results || {};
                setRankDataMap(prev => ({
                    ...prev,
                    [key]: results[key] || null,
                }));
                dispatchQuotaUpdated('rankHistoryWatch');
            }
        } catch (err) {
            dispatchQuotaUpdated('rankHistoryWatch');
            const msg = apiErrToMessage(err?.response?.data, err?.response?.status);
            message.error(msg);
        } finally {
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, 1000 - elapsed);
            setTimeout(() => {
                setRankLoadingMap(prev => {
                    const copy = { ...prev };
                    delete copy[key];
                    return copy;
                });
            }, remaining);
        }
    }, []);

    const loadList = useCallback(
        async (silent = false) => {
            if (!silent) setListLoading(true);
            try {
                const res = await AuthService.authAxios.get('/rank-history-watch');
                const newRows = res.data.rows || [];
                const sortedRows = [...newRows].sort((a, b) => {
                    if (a.created_at && b.created_at) {
                        const diffTime = new Date(b.created_at) - new Date(a.created_at);
                        if (diffTime !== 0) return diffTime;
                    }
                    return (b.id || 0) - (a.id || 0);
                });
                setRows(sortedRows);
                setMaxRows(res.data.maxRows || 20);
                await loadBatch(newRows);
            } catch {
                message.error('목록 조회 실패');
            } finally {
                if (!silent) setListLoading(false);
            }
        },
        [loadBatch]
    );

    useEffect(() => {
        loadList();
    }, [loadList]);

    const handleAdd = async () => {
        const url = urlInput.trim();
        const keyword = keywordInput.trim();

        if (!url) {
            message.warning('상품 URL을 입력하세요.');
            return;
        }
        if (!keyword) {
            message.warning('키워드를 입력하세요.');
            return;
        }

        // 🌐 네이버 도메인 유효성 검사 (스마트스토어, 브랜드스토어, 쇼핑, 단축 URL 등 대응)
        const isNaverUrl = /^(https?:\/\/)?([a-z0-9-]+\.)*(naver\.com|naver\.me)\//i.test(url);
        if (!isNaverUrl) {
            message.warning('올바른 네이버 쇼핑 또는 스마트스토어 상품 주소를 입력해 주세요.');
            return;
        }

        const mid = extractMid(url);
        if (!mid) {
            message.error(
                'URL에서 상품번호를 추출할 수 없습니다. 스마트스토어 상품 URL 또는 네이버 가격비교(catalog) URL을 입력하세요.'
            );
            return;
        }

        // 클라이언트 단 중복 체크
        const isDuplicate = rows.some(
            r =>
                String(r.product_mid) === String(mid) &&
                String(r.keyword).trim() === String(keyword).trim()
        );
        if (isDuplicate) {
            message.warning('이미 등록된 정보가 있습니다. 새로고침을 통해 확인하세요.');
            return;
        }

        setAddLoading(true);
        try {
            const res = await AuthService.authAxios.post('/rank-history-watch/add', {
                product_url: url,
                product_mid: mid,
                keyword,
            });
            if (res.data?.success && res.data.row) {
                const newRow = res.data.row;
                setRows(prev => [newRow, ...prev]);
                message.success('등록되었습니다.');
                setUrlInput('');
                setKeywordInput('');
                await loadSingleRank(newRow.keyword, newRow.product_mid, newRow.product_url);
            }
        } catch (err) {
            const msg = apiErrToMessage(err?.response?.data, err?.response?.status);
            message.error(msg);
        } finally {
            setAddLoading(false);
        }
    };

    const handleDelete = async id => {
        try {
            await AuthService.authAxios.post('/rank-history-watch/delete', { id });
            message.success('삭제되었습니다.');
            setRows(prev => prev.filter(r => r.id !== id));
        } catch {
            message.error('삭제 실패');
        }
    };

    const handleRefresh = useCallback(
        (keyword, mid, url) => {
            const key = `${keyword}__${mid}`;
            const nowKst = new Date(Date.now() + 9 * 3600 * 1000);
            const todayStr = nowKst.toISOString().slice(0, 10);
            const hasToday = rankDataMap[key]?.series?.some(s => s.date === todayStr);

            if (hasToday) {
                Modal.confirm({
                    title: '추가 조회 확인',
                    content:
                        '오늘 이미 순위를 갱신(조회)했습니다. 오늘 다시 조회하더라도 순위 변동이 없거나 미미할 수 있으며, 쿼터 한도가 추가로 1회 차감됩니다. 계속 진행하시겠습니까?',
                    okText: '확인',
                    cancelText: '취소',
                    onOk() {
                        loadSingleRank(keyword, mid, url);
                    },
                });
            } else {
                loadSingleRank(keyword, mid, url);
            }
        },
        [loadSingleRank, rankDataMap]
    );

    return (
        <ProtectedMenuPage menuKey="rankHistoryWatch">
            <div className="rank-history-watch-page">
            <div style={{ padding: '20px 24px', maxWidth: 1400, margin: '0 auto' }}>
                <Card
                    className="rank-history-search-card"
                    size="small"
                    style={{
                        position: 'sticky',
                        top: '12px',
                        zIndex: 100,
                        marginBottom: 16,
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                    }}
                    bodyStyle={{ padding: '12px 16px' }}
                >
                    <Row gutter={[8, 8]} align="bottom">
                        <Col xs={24} sm={14}>
                            <div style={{ marginBottom: 4 }}>
                                <Text style={{ fontSize: 12 }}>상품 URL</Text>
                                <Text style={{ fontSize: 11, color: '#94a3b8', marginLeft: 6 }}>
                                    (100위 내 상품만 과거 순위 조회 가능)
                                </Text>
                            </div>
                            <Input
                                placeholder="https://smartstore.naver.com/.../products/123... 또는 catalog/..."
                                value={urlInput}
                                onChange={e => setUrlInput(e.target.value)}
                                onPressEnter={handleAdd}
                                allowClear
                            />
                        </Col>
                        <Col xs={24} sm={7}>
                            <div
                                style={{
                                    marginBottom: 4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                }}
                            >
                                <Text style={{ fontSize: 12 }}>대표 키워드</Text>
                                <span style={{ fontSize: 10, color: '#f43f5e', fontWeight: 600 }}>
                                    (※ 맞춤법/오타 주의)
                                </span>
                            </div>
                            <Input
                                placeholder="정확한 키워드 입력 (맞춤법/오타 주의)"
                                value={keywordInput}
                                onChange={e => setKeywordInput(e.target.value)}
                                onPressEnter={handleAdd}
                                allowClear
                            />
                        </Col>
                        <Col xs={24} sm={3}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleAdd}
                                loading={addLoading}
                                style={{ width: '100%' }}
                            >
                                추가
                            </Button>
                        </Col>
                    </Row>
                    <div
                        style={{
                            marginTop: 8,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Space size={12} align="center" wrap>
                            <span style={{ fontSize: 12, color: '#334155', fontWeight: 500 }}>
                                등록 상품 슬롯:{' '}
                                <span style={{ color: '#4f46e5', fontWeight: 700, fontSize: 13 }}>
                                    {rows.length}
                                </span>{' '}
                                <span style={{ color: '#64748b' }}>/ {maxRows}개</span>
                            </span>
                            <span style={{ color: '#cbd5e1' }}>|</span>
                            <span style={{ fontSize: 12, color: '#64748b' }}>
                                ※ 추가/개별 새로고침 시 오늘 조회 쿼터가 차감됩니다.
                            </span>
                        </Space>
                        {rows.length > 0 && (
                            <Space size={8}>
                                <Button
                                    size="small"
                                    icon={<UpOutlined />}
                                    onClick={handleCollapseAll}
                                    style={{
                                        fontSize: 11,
                                        borderRadius: 4,
                                        color: '#4f46e5',
                                        fontWeight: 500,
                                    }}
                                >
                                    모두 접기
                                </Button>
                                <Button
                                    size="small"
                                    icon={<DownOutlined />}
                                    onClick={handleExpandAll}
                                    style={{
                                        fontSize: 11,
                                        borderRadius: 4,
                                        color: '#4f46e5',
                                        fontWeight: 500,
                                    }}
                                >
                                    모두 펼치기
                                </Button>
                            </Space>
                        )}
                    </div>
                </Card>

                {listLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin />
                    </div>
                ) : rows.length === 0 ? (
                    <div style={{ marginTop: 24 }}>
                        {/* Example State Alert Banner */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                background: '#eff6ff',
                                border: '1px solid #bfdbfe',
                                padding: '14px 18px',
                                borderRadius: '10px',
                                marginBottom: '24px',
                                color: '#1e40af',
                                fontSize: '13px',
                                fontWeight: 500,
                                boxShadow: '0 2px 6px rgba(37, 99, 235, 0.05)',
                            }}
                        >
                            <InfoCircleOutlined
                                style={{ color: '#2563eb', fontSize: '18px', flexShrink: 0 }}
                            />
                            <div>
                                현재 등록된 과거 순위 조회 항목이 없습니다. 아래{' '}
                                <b>예시 화면 (샘플 데이터)</b>을 통해 과거 순위 조회 기능(30일 일별
                                조회 이력, 변동 통계, 순위 차트)을 먼저 확인해 보세요!
                            </div>
                        </div>

                        {/* Beautiful Example Container with Demo Stamp */}
                        <div style={{ marginBottom: '16px' }}>
                            <WatchCard
                                row={{
                                    id: 'sample',
                                    keyword: '캠핑랜턴 (예시)',
                                    product_mid: '12345678901',
                                    product_url: '#',
                                    memo: '예시 상품',
                                }}
                                rankData={sampleRankData}
                                loading={false}
                                onDelete={() => {}}
                                onRefresh={() => {}}
                                collapsed={false}
                                onToggleCollapse={() => {}}
                                isSample={true}
                            />
                        </div>
                    </div>
                ) : (
                    rows.map(row => (
                        <WatchCard
                            key={row.id}
                            row={row}
                            rankData={rankDataMap[`${row.keyword}__${row.product_mid}`] || null}
                            loading={!!rankLoadingMap[`${row.keyword}__${row.product_mid}`]}
                            onDelete={handleDelete}
                            onRefresh={() =>
                                handleRefresh(row.keyword, row.product_mid, row.product_url)
                            }
                            collapsed={!!collapsedMap[row.id]}
                            onToggleCollapse={() =>
                                setCollapsedMap(prev => ({
                                    ...prev,
                                    [row.id]: !prev[row.id],
                                }))
                            }
                        />
                    ))
                )}
            </div>
            </div>
            <KakaoFloatButton />
        </ProtectedMenuPage>
    );
}
